import { getProductPricingRequirements } from "./productConfigs";

const recordedNumber = (...values) => {
  for (const value of values) {
    if (!["number", "string"].includes(typeof value) || String(value).trim() === "") continue;
    const number = Number(value);
    if (Number.isFinite(number) && number >= 0) return number;
  }
  return null;
};

// Presentation only: consume the selected live/saved breakdown, never reprice or
// reconstruct geometry. Missing legacy information remains missing; zero is valid.
export function resolveQuotePdfComponents(row = {}, calc = {}, config = {}) {
  const type = config?.calculationType || row.productConfigSnapshot?.calculationType || calc.calculationType || row.calculationType;
  const effective = { ...config, calculationType: type };
  const pricing = getProductPricingRequirements(effective);
  const snapshot = calc.usesCalculationSnapshot ? row.calculationSnapshot || calc.calculationSnapshot || {} : {};
  const formula = effective.fabricRules?.meterFormulaType || row.productConfigSnapshot?.fabricRules?.meterFormulaType;
  const isPanelFabric = pricing.usesSeparateFabricCost && ["curtain_panels", "roman_panels"].includes(formula);
  const frozen = calc.usesCalculationSnapshot === true;
  const sources = frozen ? [snapshot, row] : [calc];
  const read = fields => recordedNumber(...sources.flatMap(source => fields.map(field => source[field])));
  const usesDefaultSqftRate = !frozen && effective.sqftRules?.rateSource === "default_rate"
    && row.rateSources?.sqftRate !== "catalogue" && !["manual_override", "loaded_saved_quote"].includes(row.rateSource) && row.rateOverridden !== true;
  return {
    calculationType: type, ...pricing, isPanelFabric, usesSavedSnapshot: frozen,
    isDirectFabric: pricing.usesSeparateFabricCost && !isPanelFabric,
    customerFabricMeters: pricing.usesSeparateFabricCost ? read(["customerBillableFabricMeters", "orderedFabricMeters", "fabricMeters"]) : null,
    workshopFabricMeters: pricing.usesSeparateFabricCost ? read(["workshopFabricRequiredMeters", "workshopNewFabricMeters", "newFabricNeededMeters", "fabricMeters"]) : null,
    billableSqft: pricing.requiresSqftRate ? read(["billableSqft", "adjSqft"])
      ?? recordedNumber(snapshot.sqftCalculation?.billableSqft, row.sqftCalculation?.billableSqft) : null,
    mrpPerMeter: pricing.requiresMrpPerMeter ? recordedNumber(row.mrp, row.mrpPerMeter) : null,
    sqftRate: pricing.requiresSqftRate ? recordedNumber(usesDefaultSqftRate ? effective.sqftRules.defaultRate : null, row.sqftRate, row.ratePerSqft) : null,
  };
}

export function addDirectFabricPdfSummaries(summaries = [], entries = [], mode = "workshop") {
  const result = summaries.map(summary => ({ ...summary }));
  const groups = new Map();
  for (const { row, calc, config, fabricKey, fabricLabel } of entries) {
    const component = resolveQuotePdfComponents(row, calc, config);
    if (!component.isDirectFabric || !fabricKey) continue;
    const meters = mode === "customer" ? component.customerFabricMeters : component.workshopFabricMeters;
    const group = groups.get(fabricKey) || { meters: 0, missing: false, items: [], row, fabricLabel };
    group.missing ||= meters === null;
    group.meters += meters ?? 0;
    group.items.push(row.item || row.name || "");
    groups.set(fabricKey, group);
  }
  for (const [key, group] of groups) {
    const index = result.findIndex(summary => [summary.normalizedKey, summary.fabricKey, summary.key].includes(key));
    const prior = index >= 0 ? result[index] : {};
    // Existing panel quantities in a shared fabric group must not be replaced.
    const priorMeters = prior.pdfDirectFabricIncluded ? prior.pdfPanelFabricMeters ?? 0
      : (mode === "customer" ? recordedNumber(prior.customerBillableFabricMeters, prior.fabricToOrderMeters)
        : recordedNumber(prior.workshopFabricRequiredMeters, prior.fabricToOrderMeters)) ?? 0;
    const meters = group.missing ? null : Math.round((priorMeters + group.meters + Number.EPSILON) * 100) / 100;
    const summary = { ...prior, key, normalizedKey: key, fabricKey: key,
      fabricCode: prior.fabricCode || group.fabricLabel, displayName: prior.displayName || group.fabricLabel,
      fabricWidthIn: prior.fabricWidthIn ?? group.row.fabricWidthIn,
      usedInItems: [...new Set([...(prior.usedInItems || []), ...group.items].filter(Boolean))],
      fabricToOrderMeters: meters, workshopFabricRequiredMeters: meters, newFabricNeededMeters: meters,
      customerBillableFabricMeters: meters, pdfDirectFabricIncluded: true, pdfPanelFabricMeters: priorMeters,
      pdfFabricMetersUnavailable: meters === null };
    if (index >= 0) result[index] = summary; else result.push(summary);
  }
  return result;
}
